import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  SupplyRequestStatus,
  SupplyRequestPriority,
} from '@/types/supply-request';

interface StatusCounts {
  total: number;
  actionRequired: number;
  draft: number;
  submitted: number;
  triage: number;
  pending_approval: number;
  approved: number;
  sourcing: number;
  ordered: number;
  in_transit: number;
  delivered: number;
  partially_fulfilled: number;
  completed: number;
  cancelled: number;
  rejected: number;
}

interface SupplyRequestStoreState {
  // Filters
  statusFilter: SupplyRequestStatus | '';
  priorityFilter: SupplyRequestPriority | '';
  searchInput: string;
  search: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  dateFrom: string;
  dateTo: string;
  requestingUnitFilter: string;
  // Pagination
  page: number;
  limit: number;
  // UI toggles
  showFilters: boolean;
  showAdvancedSearch: boolean;
  showCreateModal: boolean;
  // Selection
  selectedIds: string[];
  // Status counts
  statusCounts: StatusCounts;
  statusCountsStale: boolean;
  // Socket
  socketRefreshTrigger: number;
  // Actions — filters (each resets page to 1)
  setStatusFilter: (s: SupplyRequestStatus | '') => void;
  setPriorityFilter: (p: SupplyRequestPriority | '') => void;
  setSearchInput: (s: string) => void;
  setSearch: (s: string) => void;
  setSortBy: (s: string) => void;
  setSortOrder: (o: 'asc' | 'desc') => void;
  setDateFrom: (d: string) => void;
  setDateTo: (d: string) => void;
  setRequestingUnitFilter: (u: string) => void;
  // Actions — filter utilities
  clearAllFilters: () => void;
  hasActiveFilters: () => boolean;
  activeFilterCount: () => number;
  // Actions — pagination
  setPage: (p: number) => void;
  setLimit: (l: number) => void;
  resetPagination: () => void;
  // Actions — UI toggles
  setShowFilters: (v: boolean) => void;
  setShowAdvancedSearch: (v: boolean) => void;
  setShowCreateModal: (v: boolean) => void;
  // Actions — selection
  toggleSelection: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearSelection: () => void;
  // Actions — socket
  handleSocketEvent: (type: string, requestId: string) => void;
  // Actions — status counts
  setStatusCounts: (counts: StatusCounts) => void;
  markCountsStale: () => void;
}

const defaultStatusCounts: StatusCounts = {
  total: 0,
  actionRequired: 0,
  draft: 0,
  submitted: 0,
  triage: 0,
  pending_approval: 0,
  approved: 0,
  sourcing: 0,
  ordered: 0,
  in_transit: 0,
  delivered: 0,
  partially_fulfilled: 0,
  completed: 0,
  cancelled: 0,
  rejected: 0,
};

export const useSupplyRequestStore = create<SupplyRequestStoreState>()(
  persist(
    (set, get) => ({
      // Filters
      statusFilter: '',
      priorityFilter: '',
      searchInput: '',
      search: '',
      sortBy: 'created_at',
      sortOrder: 'desc',
      dateFrom: '',
      dateTo: '',
      requestingUnitFilter: '',
      // Pagination
      page: 1,
      limit: 20,
      // UI toggles
      showFilters: false,
      showAdvancedSearch: false,
      showCreateModal: false,
      // Selection
      selectedIds: [],
      // Status counts
      statusCounts: defaultStatusCounts,
      statusCountsStale: false,
      // Socket
      socketRefreshTrigger: 0,

      // Actions — filters (each resets page to 1)
      setStatusFilter: (s) => set({ statusFilter: s, page: 1 }),
      setPriorityFilter: (p) => set({ priorityFilter: p, page: 1 }),
      setSearchInput: (s) => set({ searchInput: s }),
      setSearch: (s) => set({ search: s, page: 1 }),
      setSortBy: (s) => set({ sortBy: s, page: 1 }),
      setSortOrder: (o) => set({ sortOrder: o, page: 1 }),
      setDateFrom: (d) => set({ dateFrom: d, page: 1 }),
      setDateTo: (d) => set({ dateTo: d, page: 1 }),
      setRequestingUnitFilter: (u) => set({ requestingUnitFilter: u, page: 1 }),

      // Actions — filter utilities
      clearAllFilters: () =>
        set({
          statusFilter: '',
          priorityFilter: '',
          searchInput: '',
          search: '',
          sortBy: 'created_at',
          sortOrder: 'desc',
          dateFrom: '',
          dateTo: '',
          requestingUnitFilter: '',
          page: 1,
        }),
      hasActiveFilters: () => {
        const state = get();
        return (
          !!state.search ||
          !!state.statusFilter ||
          !!state.priorityFilter ||
          !!state.dateFrom ||
          !!state.dateTo ||
          !!state.requestingUnitFilter
        );
      },
      activeFilterCount: () => {
        const state = get();
        let count = 0;
        if (state.search) count++;
        if (state.statusFilter) count++;
        if (state.priorityFilter) count++;
        if (state.dateFrom) count++;
        if (state.dateTo) count++;
        if (state.requestingUnitFilter) count++;
        return count;
      },

      // Actions — pagination
      setPage: (p) => set({ page: p }),
      setLimit: (l) => set({ limit: l, page: 1 }),
      resetPagination: () => set({ page: 1 }),

      // Actions — UI toggles
      setShowFilters: (v) => set({ showFilters: v }),
      setShowAdvancedSearch: (v) => set({ showAdvancedSearch: v }),
      setShowCreateModal: (v) => set({ showCreateModal: v }),

      // Actions — selection
      toggleSelection: (id) => {
        const { selectedIds } = get();
        if (selectedIds.includes(id)) {
          set({ selectedIds: selectedIds.filter((sid) => sid !== id) });
        } else {
          set({ selectedIds: [...selectedIds, id] });
        }
      },
      selectAll: (ids) => set({ selectedIds: ids }),
      clearSelection: () => set({ selectedIds: [] }),

      // Actions — socket
      handleSocketEvent: (_type, _requestId) => {
        set((state) => ({
          socketRefreshTrigger: state.socketRefreshTrigger + 1,
          statusCountsStale: true,
        }));
      },

      // Actions — status counts
      setStatusCounts: (counts) => set({ statusCounts: counts, statusCountsStale: false }),
      markCountsStale: () => set({ statusCountsStale: true }),
    }),
    {
      name: 'supply-request-store',
      version: 2,
      partialize: (state) => ({
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
        showFilters: state.showFilters,
        limit: state.limit,
      }),
      migrate: (persistedState: unknown) => persistedState,
    }
  )
);
